import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import HelpReader, { Marked } from '../../components/help/HelpReader';
import { errorOf } from '../../lib/docs';
import { helpApi, useHelpMe, day, editHref, helpHref } from '../../lib/help';
import '../../components/docs/Docs.css';
import '../../components/help/Help.css';

/**
 * Help (migration 123): plain-language answers for every member of staff.
 *
 *   /help            the categories, most read and recently updated; and
 *   /help?q=         search - Help only, published articles only
 *   /help/:id        one article
 *   /help/admin      the overview, for administrators and Help editors
 *
 * Documentation's engine underneath (lib/docs-access.js decides who reads
 * and who edits); none of its runbook words up here. Editing is
 * Documentation's editor, one click away for whoever may edit.
 */

const SEARCH_WAIT_MS = 300;

function NotReady({ me }) {
  return (
    <section className="hp-empty" role="status">
      <h1 className="hp-title">Help is not set up yet</h1>
      {me?.is_admin
        ? <p>This server needs migration 123 (<code>npm run migrate:123</code>). Help appears for everybody once it has run.</p>
        : <p>Your administrator has not finished setting up Help.</p>}
    </section>
  );
}

function SearchBox({ value, onChange }) {
  return (
    <form className="hp-search" role="search" onSubmit={(e) => e.preventDefault()}>
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
        <circle cx="8.5" cy="8.5" r="5.5" /><path d="m13 13 4 4" />
      </svg>
      <label htmlFor="hp-q" className="sr-only">Search Help</label>
      <input id="hp-q" type="search" placeholder="Search Help - for example “printer” or “VPN”" value={value}
             onChange={(e) => onChange(e.target.value)} autoComplete="off" maxLength={200} />
    </form>
  );
}

function Results({ q }) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  // Only the answer to the latest search is shown: an earlier, slower one
  // arriving after it is dropped.
  const seq = useRef(0);
  useEffect(() => {
    const n = ++seq.current;
    setRes(null); setErr(null);
    helpApi.get('/help/search', { q })
      .then((r) => { if (n === seq.current) setRes(r); })
      .catch((e) => { if (n === seq.current) setErr(errorOf(e)); });
  }, [q]);
  if (err) return <p className="dp-error" role="alert">{err}</p>;
  if (!res) return <p className="dp-hint" aria-busy="true">Searching…</p>;
  return (
    <section className="hp-results" aria-label="Search results">
      <p className="hp-count" role="status">
        {res.results.length
          ? `${res.results.length} answer${res.results.length === 1 ? '' : 's'} for “${q}”`
          : <>No answers for “{q}”. Try other words, or <Link to="/create">raise a ticket</Link>.</>}
      </p>
      <ol className="hp-result-list">
        {res.results.map((r) => (
          <li key={r.id}>
            <Link to={helpHref(r.id)} className="hp-result">
              <span className="hp-result-title">{r.title}</span>
              {r.category && <span className="hp-result-cat">{r.category.title}</span>}
              {r.snippet && <span className="hp-result-snip"><Marked text={r.snippet} /></span>}
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Category({ c }) {
  const [all, setAll] = useState(false);
  const shown = all ? c.articles : c.articles.slice(0, 5);
  return (
    <section className="hp-cat" aria-labelledby={`hp-cat-${c.id || 'more'}`}>
      <h2 className="hp-cat-title" id={`hp-cat-${c.id || 'more'}`}>{c.title}</h2>
      {c.description && <p className="hp-cat-desc">{c.description}</p>}
      <ul className="hp-links">
        {shown.map((a) => <li key={a.id}><Link to={helpHref(a.id)}>{a.title}</Link></li>)}
      </ul>
      {c.articles.length > 5 && (
        <button type="button" className="btn btn-plain btn-small" onClick={() => setAll(!all)} aria-expanded={all}>
          {all ? 'Show fewer' : `Show all ${c.count}`}
        </button>
      )}
    </section>
  );
}

function Home({ canEdit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlQ = new URLSearchParams(location.search).get('q') || '';
  const [typed, setTyped] = useState(urlQ);
  const [home, setHome] = useState(null);
  const [err, setErr] = useState(null);
  const timer = useRef(null);

  // Back and Forward (and a link to /help?q=) move the box with the address.
  // Not when it is our own write catching up with what is typed (a
  // trailing space would be taken away mid-word).
  useEffect(() => { setTyped((t) => (t.trim() === urlQ ? t : urlQ)); }, [urlQ]);
  useEffect(() => {
    let live = true;
    helpApi.get('/help/home').then((r) => { if (live) setHome(r); }).catch((e) => { if (live) setErr(errorOf(e)); });
    return () => { live = false; };
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onType = (v) => {
    setTyped(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      // From the live address, not the router's copy, which can lag a
      // navigation behind (the licences lesson).
      const p = new URLSearchParams(window.location.search);
      if (v.trim()) p.set('q', v.trim()); else p.delete('q');
      const qs = p.toString();
      navigate(`/help${qs ? `?${qs}` : ''}`, { replace: true });
    }, SEARCH_WAIT_MS);
  };

  return (
    <div className="hp-home">
      <header className="hp-hero">
        <div>
          <h1 className="hp-title">Help</h1>
          <p className="hp-lede">Answers to everyday questions about your laptop, email, Wi-Fi and more. Can’t find it? <Link to="/create">Raise a ticket</Link>.</p>
        </div>
        {canEdit && <Link to="/help/admin" className="btn btn-outline btn-small hp-manage">Manage Help</Link>}
      </header>
      <SearchBox value={typed} onChange={onType} />
      {urlQ ? <Results q={urlQ} /> : (
        <>
          {err && <p className="dp-error" role="alert">{err}</p>}
          {!home && !err && <p className="dp-hint" aria-busy="true">Loading…</p>}
          {home && home.total === 0 && (
            <section className="hp-empty" role="status">
              <p>Nothing has been published in Help yet.</p>
              {canEdit && <p><Link to="/help/admin">Open the Help overview</Link> to review the starter drafts and publish them.</p>}
            </section>
          )}
          {home && home.total > 0 && (
            <>
              <div className="hp-cats">
                {home.categories.map((c) => <Category key={c.id || 'more'} c={c} />)}
              </div>
              <div className="hp-lists">
                {home.popular.length > 0 && (
                  <section className="hp-list" aria-labelledby="hp-popular">
                    <h2 className="hp-side-title" id="hp-popular">Popular</h2>
                    <ul className="hp-links">{home.popular.map((a) => <li key={a.id}><Link to={helpHref(a.id)}>{a.title}</Link></li>)}</ul>
                  </section>
                )}
                <section className="hp-list" aria-labelledby="hp-recent">
                  <h2 className="hp-side-title" id="hp-recent">Recently updated</h2>
                  <ul className="hp-links">
                    {home.recent.map((a) => <li key={a.id}><Link to={helpHref(a.id)}>{a.title}</Link> <span className="hp-when">{day(a.updated_at)}</span></li>)}
                  </ul>
                </section>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function Tile({ label, value, sub }) {
  return <div className="hp-tile"><span className="hp-tile-label">{label}</span><strong className="hp-tile-value">{value}</strong>{sub && <span className="hp-tile-sub">{sub}</span>}</div>;
}

function Admin() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    helpApi.get('/help/admin').then((r) => { if (live) setData(r); })
      .catch((e) => { if (live) setErr(e?.response?.status === 403 ? 'forbidden' : errorOf(e)); });
    return () => { live = false; };
  }, []);
  if (err === 'forbidden') {
    return <section className="hp-empty" role="status"><h1 className="hp-title">Help overview</h1><p>Only administrators and Help editors see this.</p><p><Link to="/help">Back to Help</Link></p></section>;
  }
  if (err) return <p className="dp-error" role="alert">{err}</p>;
  if (!data) return <p className="dp-hint" aria-busy="true">Loading…</p>;
  const t = data.totals;
  const manageable = data.spaces.find((s) => s.can_manage);
  return (
    <div className="hp-admin">
      <nav className="hp-crumbs" aria-label="Breadcrumb"><Link to="/help">Help</Link><span aria-hidden="true"> › </span><span>Overview</span></nav>
      <h1 className="hp-title">Help overview</h1>
      {data.starters_added > 0 && (
        <p className="hp-notice" role="status">
          {data.starters_added} starter drafts were added. Nobody else sees them until they are reviewed and published.
        </p>
      )}
      <div className="hp-tiles">
        <Tile label="Published" value={t.published} />
        <Tile label="Drafts" value={t.drafts} sub="not yet seen by staff" />
        <Tile label="Read" value={t.views30} sub="in the last 30 days" />
        <Tile label="Helpful" value={`${t.yes} yes · ${t.no} no`} />
        <Tile label="Solved without a ticket" value={t.deflections} sub={`${t.deflections30} in the last 30 days`} />
      </div>

      <section className="hp-panel" aria-labelledby="hp-drafts">
        <h2 className="hp-side-title" id="hp-drafts">To review and publish</h2>
        {data.drafts.length === 0 ? <p className="dp-hint">Nothing waiting. Every article is published.</p> : (
          <ul className="hp-rows">
            {data.drafts.map((d) => (
              <li key={d.id} className="hp-row">
                <span className="hp-row-main">
                  <span className="hp-row-title">{d.title}</span>
                  <span className="hp-row-sub">{[d.category, `saved ${day(d.updated_at)}`].filter(Boolean).join(' · ')}</span>
                  {d.hint && <span className="hp-row-hint">{d.hint}</span>}
                </span>
                <span className={`hp-pill${d.starter ? ' is-review' : ''}`}>{d.label}</span>
                {d.can_edit && <Link className="btn btn-outline btn-small" to={editHref(d.id)}>Edit</Link>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="hp-two">
        <section className="hp-panel" aria-labelledby="hp-top-defl">
          <h2 className="hp-side-title" id="hp-top-defl">Solved without a ticket</h2>
          <p className="hp-hint">Suggested on Create Ticket, opened, and no ticket raised.</p>
          {data.top_deflections.length === 0 ? <p className="dp-hint">None yet.</p> : (
            <ol className="hp-rank">
              {data.top_deflections.map((r) => <li key={r.id}><Link to={helpHref(r.id)}>{r.title}</Link><span>{r.total} ({r.last30} in 30 days)</span></li>)}
            </ol>
          )}
        </section>
        <section className="hp-panel" aria-labelledby="hp-top-help">
          <h2 className="hp-side-title" id="hp-top-help">Most helpful</h2>
          <p className="hp-hint">Readers who answered “Was this helpful?”.</p>
          {data.top_helpful.length === 0 ? <p className="dp-hint">No votes yet.</p> : (
            <ol className="hp-rank">
              {data.top_helpful.map((r) => <li key={r.id}><Link to={helpHref(r.id)}>{r.title}</Link><span>{r.yes} yes · {r.no} no</span></li>)}
            </ol>
          )}
        </section>
      </div>

      <section className="hp-panel" aria-labelledby="hp-editors">
        <h2 className="hp-side-title" id="hp-editors">Who edits Help</h2>
        <p className="hp-hint">Administrators always. Anybody else is given Edit on the Help space - IT staff included, who do not edit Help unless named here.</p>
        {data.editors.length > 0 && <ul className="hp-links">{data.editors.map((e, i) => <li key={i}>{e.label} <span className="hp-when">{e.level}</span></li>)}</ul>}
        {manageable && <p><Link to={`/docs/${manageable.id}`}>Change who edits Help</Link> (Permissions on the space).</p>}
      </section>
    </div>
  );
}

export default function HelpPage({ view = 'home' }) {
  const { id } = useParams();
  const me = useHelpMe(true);
  if (!me) return <div className="container hp-page"><p className="dp-hint" aria-busy="true">Loading…</p></div>;
  return (
    <div className="container hp-page" data-view={view}>
      {me.ready === false && !me.failed ? <NotReady me={me} />
        : view === 'admin' ? <Admin />
          : view === 'article' ? <HelpReader key={id} id={id} />
            : <Home canEdit={Boolean(me.can_edit)} />}
    </div>
  );
}
